import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import apiService from "../service/Api";

interface ISystemUpdate {
  id: number;
  title: string;
  summary: string;
  author: string;
  category: string;
  category_display: string;
  pr_url: string | null;
  created_at: string;
}

const CATEGORY_COLORS: Record<
  string,
  "default" | "primary" | "success" | "warning" | "error" | "info"
> = {
  bookings: "primary",
  streams: "info",
  equipment: "success",
  callsheet: "warning",
  contacts: "default",
  venues: "error",
  configurations: "default",
  other: "default",
};

const Changelog = () => {
  const [updates, setUpdates] = useState<ISystemUpdate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService
      .get<{ results: ISystemUpdate[] }>("/api/configurations/system-updates/")
      .then((res) => setUpdates(res.data.results))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 4, px: 2 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        What's New
      </Typography>

      {updates.length === 0 ? (
        <Typography color="text.secondary">No updates yet.</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={3}>
          {updates.map((update) => (
            <Box key={update.id}>
              <Typography variant="caption" color="text.secondary">
                {new Date(update.created_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              <Stack direction="row" spacing={1} mt={0.5} mb={1}>
                <Chip
                  label={update.category_display}
                  size="small"
                  color={CATEGORY_COLORS[update.category] ?? "default"}
                />
                <Chip
                  label={`by ${update.author}`}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Typography variant="subtitle1" fontWeight={600}>
                {update.title}
              </Typography>

              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {update.summary}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Changelog;
